import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { achievementById, levelName } from "core/src/achievements/achievements.js";
import { levelColor } from "./UnlockCelebration";
import musicController from "../../../audio/music-controller";
import GoldenEggIcon from "../../../shared/GoldenEggIcon";
import styles from "./AchievementProgress.module.scss";

// After a match: what it moved on your achievements, as small cards in the
// lower right corner, at most five (the server picks them: core progressMoves,
// the levels reached first, then the ones nearest their next level). Each card
// wears the achievement's medal in its level's colour; the bar shows what the
// match added as a brighter piece that grows; a level reached says Unlocked,
// in gold, with the eggs it waits to give (opened on the start page or the
// profile). They come in one after another with a soft swish (an unlocked one
// with the achievement's bell), stay a while, then slide away; a click sends
// one away sooner. `inline` keeps them inside the parent and `compact` draws
// the phone's size (the design lab).

export interface AchievementMove {
  id: string;
  from: number; to: number;           // the progress before and after the match
  level: number; levelBefore: number; // levels reached, after and before
  start: number;                      // the reached level's threshold (0 before the first)
  next: number | null;                // the next level's, null when it was the last
}

const ENTER_MS = 2200;  // after the match (once the XP in the end modal has mostly counted)
const STEP_MS = 260;
const STAY_MS = 9000;
const fmt = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

function Card({ m, delay, onGone, sound }: { m: AchievementMove; delay: number; onGone: () => void; sound: boolean }) {
  const a = achievementById(m.id);
  const up = m.level > m.levelBefore;
  // what the levels reached wait to give (opened on the start page or the profile)
  const eggs = a && up ? (a.eggs as number[]).slice(m.levelBefore, m.level).reduce((n, e) => n + e, 0) : 0;
  const [grown, setGrown] = useState(false);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const s = window.setTimeout(() => { if (sound) { try { musicController.play(up ? "achievement" : "card-in"); } catch { /* no audio */ } } }, delay);
    const g = window.setTimeout(() => setGrown(true), delay + 380);
    const l = window.setTimeout(() => setLeaving(true), delay + STAY_MS);
    return () => { window.clearTimeout(s); window.clearTimeout(g); window.clearTimeout(l); };
  }, [delay, up, sound]);
  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(onGone, 350);
    return () => window.clearTimeout(id);
  }, [leaving, onGone]);
  if (!a) return null;
  const levels = a.levels.length;
  // The bar: the level being worked on. A level reached fills the one it was on.
  const lo = (lv: number) => (lv > 0 ? a.levels[lv - 1] : 0);
  const fromPct = up
    ? Math.max(0, Math.min(100, ((m.from - lo(m.levelBefore)) / Math.max(1, a.levels[m.levelBefore] - lo(m.levelBefore))) * 100))
    : m.next == null ? 100 : Math.max(0, Math.min(100, ((m.from - m.start) / Math.max(1, m.next - m.start)) * 100));
  const toPct = up || m.next == null ? 100 : Math.max(0, Math.min(100, ((m.to - m.start) / Math.max(1, m.next - m.start)) * 100));
  const shownLevel = up ? m.level : Math.max(1, m.levelBefore);
  const color = levelColor(levels > 1 ? shownLevel : 3);
  const reached = up || m.levelBefore > 0;
  return (
    <button type="button" className={`${styles.card} ${up ? styles.up : ""} ${leaving ? styles.leaving : ""}`} style={{ animationDelay: `${delay}ms`, "--ac": color } as React.CSSProperties} onClick={() => setLeaving(true)}>
      <span className={`${styles.medal} ${reached ? styles.medalOn : ""} ${up && grown ? styles.medalPop : ""}`} aria-hidden="true">
        <span className={styles.icon}>{a.icon}</span>
      </span>
      <span className={styles.main}>
        <span className={styles.top}>
          <b>{up ? levelName(a, m.level) : levels > 1 && m.levelBefore > 0 ? levelName(a, m.levelBefore) : a.name}</b>
          {up ? <em className={styles.unlocked}>Unlocked</em> : <em>{m.next == null ? "Max" : `${fmt(m.to)} / ${fmt(m.next)}`}</em>}
        </span>
        <span className={styles.track}>
          <i className={styles.was} style={{ width: `${fromPct}%` }} />
          <i className={styles.add} style={{ left: `${fromPct}%`, width: `${Math.max(0, (grown ? toPct : fromPct) - fromPct)}%` }} />
        </span>
        <span className={styles.foot}>
          <span className={styles.plus}>+{fmt(m.to - m.from)}</span>
          {up && eggs > 0 && <span className={styles.eggs}>+{eggs}<GoldenEggIcon size={13} /></span>}
        </span>
      </span>
    </button>
  );
}

export default function AchievementProgress({ moves, inline, sound = true, compact }: { moves: AchievementMove[] | null | undefined; inline?: boolean; sound?: boolean; compact?: boolean }) {
  const list = (moves || []).filter((m) => achievementById(m.id)).slice(0, 5);
  const [gone, setGone] = useState<Record<string, boolean>>({});
  if (!list.length) return null;
  const stack = (
    <div className={`${styles.stack} ${inline ? styles.inline : ""} ${compact ? styles.compact : ""}`} aria-live="polite">
      {list.map((m, i) => gone[m.id] ? null : (
        <Card key={m.id} m={m} delay={ENTER_MS + i * STEP_MS} sound={sound} onGone={() => setGone((g) => ({ ...g, [m.id]: true }))} />
      ))}
    </div>
  );
  return inline ? stack : createPortal(stack, document.body);
}
